import { Component, ViewChild } from '@angular/core';
import { CampaignSettingsAbstractComponent } from '../campaign-settings-abstract.component';
import {
  Campaign,
  CampaignSection,
  ContactFormField,
  DefaultContactFormFields,
  EmailSupportFieldTypes,
  Region,
  RequiredEmailSupportFields,
  ShowDeclinedOrders,
  CancelItemsSelection,
  CustomerPortalSettingHints,
  CustomerPortalSettingType,
  User,
  RestockFeeCalcType,
  RestockFeeType,
  ShippingSystem,
  Funnel,
  Pager,
  FunnelType,
  ShowBreadcrumbs,
  AutoCancelUpsellType,
} from '../../_models';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  AlertService,
  CampaignService,
  LoaderService,
  RegionService,
  ShippingSystemService,
  SideNavService,
  UserService,
  FunnelService,
} from '../../_services';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntil } from 'rxjs/operators';
import { fadeIn } from '../../_shared';
import { ContactFieldPopupComponent } from '../campaign-call-center/contact-field-popup.component';
import { config } from '../../../config/config';

@Component({
  templateUrl: './campaign-customer-portal-settings.component.html',
  styleUrls: [
    '../campaign-section.scss',
    './campaign-customer-portal-settings.component.scss',
    '../../customer-portal/customer-portal.component.scss',
    '../campaign-call-center/campaign-call-center-settings.component.scss',
  ],
  animations: [fadeIn],
})
export class CampaignCustomerPortalSettingsComponent extends CampaignSettingsAbstractComponent {
  public section = CampaignSection.CustomerPortalSettings;
  CustomerPortalSettingHints = CustomerPortalSettingHints;
  showDeclinedOrdersSettings = [
    { value: ShowDeclinedOrders.FailedRebillsOnly, label: 'Failed Rebills Only' },
    { value: ShowDeclinedOrders.Always, label: 'Always' },
    { value: ShowDeclinedOrders.Never, label: 'Never' },
  ];
  public blacklistedStatesFilterSettings = {
    singleSelection: false,
    allowSearchFilter: true,
    enableCheckAll: false,
    textField: 'name',
  };
  public allowedCountriesFilterSettings = {
    singleSelection: false,
    allowSearchFilter: true,
    enableCheckAll: false,
    textField: 'name',
  };
  public states: Region[] = [];
  public countries: Region[] = [];
  public renderStateSelection: boolean = false;
  public billingOptions: number[] = Array.from({ length: 5 }, (_, i) => i + 1);
  cancelItemsSelections = [
    { value: CancelItemsSelection.UpsellsOnlyIfUpsell, label: 'Show Upsells Only if Upsell, Otherwise Show All' },
    { value: CancelItemsSelection.UpsellsOnly, label: 'Show Upsells Only' },
    { value: CancelItemsSelection.ShowAll, label: 'Show All' },
    { value: CancelItemsSelection.ShowNone, label: 'Show None' },
  ];
  protected readonly AutoCancelUpsellType = AutoCancelUpsellType;
  showBreadcrumbSelections = [
    { value: ShowBreadcrumbs.OnlyIfCanUndo, label: 'Only if step can go back' },
    { value: ShowBreadcrumbs.Always, label: 'Always' },
    { value: ShowBreadcrumbs.Never, label: 'Never' },
  ];
  customerPortalHints = CustomerPortalSettingHints;
  customerPortalSettingType = CustomerPortalSettingType;
  user: User = null;
  public itemStatusSettings = {
    enableCheckAll: false,
  };
  public pauseDays = [30, 45, 60, 90];
  protected expandedStates: boolean[] = [
    false, //General Settings (0)
    false, //Order Settings (1)
    false, //Subscription Settings (2)
    false, //Payment Settings (3)
    false, //Return Settings (4)
    false, //Lifeline Settings (5)
  ];
  restockFeeCalcTypes = [
    { value: RestockFeeCalcType.Max, label: 'Maximum' },
    { value: RestockFeeCalcType.Sum, label: 'Sum' },
  ];
  restockFeeTypes = [
    { value: RestockFeeType.Fixed, label: 'Fixed' },
    { value: RestockFeeType.Percent, label: 'Percent' },
  ];
  shippingSystems: ShippingSystem[] = [];
  lifelines: Funnel[] = [];
  public supportEmailFields: FormArray;
  public selectedContactField: ContactFormField = null;
  public isEditableField: boolean = true;
  @ViewChild('contactFieldPopupRef', { static: false }) contactFieldPopupRef: ContactFieldPopupComponent = null;
  public fieldTypeOptions = [
    { value: EmailSupportFieldTypes.Text, label: 'Text Input' },
    { value: EmailSupportFieldTypes.Email, label: 'Email' },
    { value: EmailSupportFieldTypes.Phone, label: 'Phone' },
    { value: EmailSupportFieldTypes.Select, label: 'Select Dropdown' },
    { value: EmailSupportFieldTypes.Textarea, label: 'Text Area' },
  ];
  public emailSupportFieldTypes = EmailSupportFieldTypes;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected campaignService: CampaignService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected sideNav: SideNavService,
    protected modalService: NgxSmartModalService,
    protected loaderService: LoaderService,
    protected regionService: RegionService,
    protected userService: UserService,
    protected shippingSystemService: ShippingSystemService,
    protected funnelService: FunnelService
  ) {
    super(router, location, route, campaignService, sideNav, alertService, loaderService);
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      max_order_days: [null, [Validators.min(0), Validators.max(1000)]],
      always_show_active_orders: [true],
      show_declined_orders: [false],
      show_replaced_items: [false],
      blacklisted_states: [[], null],
      show_order_summary: [true],
      allowed_countries: [[], null],
      mouseflow_enabled: [false],
      show_breadcrumbs: [ShowBreadcrumbs.OnlyIfCanUndo, Validators.required],
      portal_settings: this.formBuilder.group({
        min_billing_cycle_for_cancel: [1],
        show_fulfillment_status: [true],
        show_subscription_status: [true],
        show_tracking_in_list: [true],
        show_tracking_in_details: [true],
        show_transactions_in_list: [true],
        show_transactions_in_details: [true],
        min_billing_cycle_for_cancel_subscription: [1],
        allow_edit_payment_method: [true],
        allow_edit_shipping_address: [true],
        allow_edit_billing_interval: [true],
        max_pause_days: [null],
        allow_revision: [true],
        show_next_bill_date: [true],
        allow_reactivate_subscription: [true],
        show_price_in_list: [false],
        show_price_in_details: [true],
        show_taxes: [true],
        show_shipping_price: [true],
        show_payment_status_in_list: [true],
        show_payment_status_in_details: [true],
        allow_revise_subscription: [true],
        allow_cancel_trial: [true],
        allow_return_trial: [true],
        allow_extend_trial: [true],
        allow_adjust_trial_frequency: [true],
        allow_bill_trial_now: [true],
        allow_return: [true],
        allow_return_subscription: [true],
        allow_cancel_subscription_trial: [true],
        allow_buy_again: [true],
        auto_select_single_item: [true],
        hidden_fees_label: ['Processing and Handling', Validators.required],
        cancel_items_selection: [CancelItemsSelection.UpsellsOnlyIfUpsell, Validators.required],
        return_items_selection: [CancelItemsSelection.ShowAll, Validators.required],
        lifeline_timeout: [300, Validators.min(0)],
        lifeline_undo_count: [7, Validators.min(0)],
        lifeline_unmatched_count: [3, [Validators.min(1), Validators.max(1000)]],
        lifeline: [null],
        error_lifeline: [null],
        exit_lifeline: [null],
        search_lifeline: [null],
        unmatched_lifeline: [null],
        subscription_lifeline: [null],
        cancel_subscription_min_days: [0, Validators.min(0)],
        show_offer_details: [false],
        show_shipping_processing: [false],
        show_return_pending: [false],
        show_resolve_refund: [false]
      }),
      auto_cancel_upsells: [AutoCancelUpsellType.AfterAllMainProducts, Validators.required],
      return_days: [null, [Validators.required, Validators.min(0)]],
      exchange_days: [null, [Validators.required, Validators.min(0)]],
      max_extra_return_days: [30, [Validators.required, Validators.min(0)]],
      restock_fee: [null, Validators.min(0)],
      rma_shipping_fee: [null, Validators.min(0)],
      restock_fee_calc_type: [RestockFeeCalcType.Max, Validators.required],
      restock_fee_type: [RestockFeeType.Fixed, Validators.required],
      shipping_system: [null],
      support_email_fields: this.formBuilder.array([]),
    });

    this.supportEmailFields = this.form.get('support_email_fields') as FormArray;

    super.ngOnInit();

    this.regionService.list('us').subscribe((states: Region[]) => {
      this.states = states;
      this.updateRegions();
    });

    this.regionService.getCountries().subscribe((countries: Region[]) => {
      this.countries = countries;
      this.updateRegions();
    });

    this.userService.getCurrent().subscribe(
      (user: User) => {
        this.user = user;
      },
      (error) => {
        this.handleError(error);
      }
    );

    this.shippingSystemService.list({ page: 1, page_size: config.maxPageSize }).subscribe((data) => {
      this.shippingSystems = data.results;
    });

    this.funnelService
      .list({ resourcetype: FunnelType.Lifeline, page: 1, page_size: config.maxPageSize })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: Pager) => {
          data.results.forEach((funnel: Funnel) => {
            this.lifelines.push(funnel);
          });
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  onCampaignFetch(data: Campaign) {
    if (!data.support_email_fields) {
      data.support_email_fields = [];
    }
    this.form.patchValue(data);
    this.renderStateSelection = true;
    this.updateRegions();

    data.support_email_fields.forEach((field: ContactFormField) => {
      this.addContactFormField(field.name, field.label, field.type, field.required, field.values);
    });
    this.addDefaultContactFormFields();
  }

  protected updateRegions() {
    if (this.campaign && this.states && this.states.length && this.countries && this.countries.length) {
      let customFormValues = { blacklisted_states: [], allowed_countries: [] };

      if (this.campaign.blacklisted_states && this.campaign.blacklisted_states.length > 0) {
        let mappedStates: Region[] = this.states.filter(
          (item: Region) => this.campaign.blacklisted_states.indexOf(item.id) > -1
        );

        mappedStates.forEach((state) => {
          customFormValues.blacklisted_states.push(state);
        });
      }

      if (this.campaign.allowed_countries && this.campaign.allowed_countries.length > 0) {
        let mappedCountries: Region[] = this.countries.filter(
          (item: Region) => this.campaign.allowed_countries.indexOf(item.id.toUpperCase()) > -1
        );

        mappedCountries.forEach((country) => {
          customFormValues.allowed_countries.push(country);
        });
      }

      this.form.patchValue(customFormValues);
    }
  }

  protected getFormData(): any {
    const selectedBlacklistedStates = this.form.value.blacklisted_states;
    let blackListedStates = [];

    if (selectedBlacklistedStates) {
      selectedBlacklistedStates.forEach((state: Region) => {
        blackListedStates.push(state.id);
      });
    }

    const selectedCountries = this.form.value.allowed_countries;
    let countries = [];

    if (selectedCountries) {
      selectedCountries.forEach((country: Region) => {
        countries.push(country.id.toUpperCase());
      });
    }

    const portalSettings = { ...this.form.value.portal_settings };

    let contactFormFields = [];
    if (this.form.value.support_email_fields && this.form.value.support_email_fields.length) {
      this.form.value.support_email_fields.forEach((field) => {
        if (field.values && field.values.length) {
          let val = Array.isArray(field.values) ? field.values.join(',') : field.values;
          val = typeof val === 'string' ? val.trim() : val;
          if (field.type === this.emailSupportFieldTypes.Select) {
            field.values =
              typeof val === 'string' && val.length
                ? val
                    .split(',')
                    .map((option) => option.trim())
                    .filter((option) => option.length > 0)
                : [];
          }
        }
        contactFormFields.push(field);
      });
    }

    return Object.assign({}, this.form.value, {
      blacklisted_states: blackListedStates,
      allowed_countries: countries,
      portal_settings: portalSettings,
      support_email_fields: contactFormFields,
    });
  }

  private sectionIdMap: { [key: number]: string } = {
    [CustomerPortalSettingType.General]: 'general-settings',
    [CustomerPortalSettingType.Order]: 'order-settings',
    [CustomerPortalSettingType.Subscription]: 'subscription-settings',
    [CustomerPortalSettingType.Payment]: 'payment-settings',
    [CustomerPortalSettingType.Lifeline]: 'lifeline-settings',
  };

  getSectionId(settingType: number): string {
    return this.sectionIdMap[settingType] || '';
  }

  togglePortalSection(settingType: number) {
    this.expandedStates = this.expandedStates.map((state, i) => {
      return i === settingType ? !state : false;
    });

    const sectionClass = this.getSectionId(settingType);
    const sectionElements = document.getElementsByClassName(sectionClass);

    if (sectionElements.length > 0) {
      const sectionElement = sectionElements[0];
      if (sectionElement && settingType !== CustomerPortalSettingType.General) {
        setTimeout(() => {
          window.scrollTo({
            top: sectionElement.getBoundingClientRect().top + window.scrollY - 100,
            behavior: 'smooth',
          });
        }, 50);
      }
    }
  }

  isPortalSectionExpanded(index: number): boolean {
    return this.expandedStates[index];
  }

  isCancelToggleChecked(fieldPath: string): boolean {
    const value = this.getControlValue('portal_settings.' + fieldPath);
    return value !== null && value !== undefined && value > 0;
  }

  toggleCancelField(fieldPath: string, event: any) {
    const value = event.target.checked ? 1 : 0;
    this.form.patchValue({ portal_settings: { [fieldPath]: value } });
  }

  get isSkipShipmentChecked(): boolean {
    const value = this.getControlValue('portal_settings.max_pause_days');
    return value !== 0;
  }

  toggleSkipShipment(event: any) {
    const value = event.target.checked ? 30 : 0;
    this.form.patchValue({ portal_settings: { max_pause_days: value } });
  }

  toggleLifeline(event: any, type: string) {
    const portalSettings = this.form.get('portal_settings');
    portalSettings.patchValue({
      [type]: event.target.checked ? this.lifeLineFirstOption : null,
    });
    this.form.updateValueAndValidity();
  }

  get generalLifeline(): boolean {
    return this.getControlValue('portal_settings.lifeline');
  }

  get exitLifeline(): boolean {
    return this.getControlValue('portal_settings.exit_lifeline');
  }

  get searchLifeline(): boolean {
    return this.getControlValue('portal_settings.search_lifeline');
  }

  get subscriptionLifeline(): boolean {
    return this.getControlValue('portal_settings.subscription_lifeline');
  }

  get lifeLineFirstOption(): number | null {
    return this.lifelines && this.lifelines.length > 0 ? +this.lifelines[0].id : null;
  }

  toggleTrackShipment(event: any) {
    const portalSettings = this.form.get('portal_settings');
    portalSettings.patchValue({
      show_tracking_in_list: event.target.checked,
      show_tracking_in_details: event.target.checked,
    });
  }

  toggleShowOrderPrice(event: any) {
    const portalSettings = this.form.get('portal_settings');
    portalSettings.patchValue({
      show_price_in_list: event.target.checked,
      show_price_in_details: event.target.checked,
    });
  }

  onShippingSystemChange(event: any) {
    const selectedValue = event.target.value;

    if (selectedValue === 'new') {
      const url = this.router.serializeUrl(this.router.createUrlTree(['/shipping-system/new']));

      window.open(url, '_blank');

      if (this.campaign.shipping_system) {
        this.form.get('shipping_system').patchValue(this.campaign.shipping_system);
      } else {
        this.form.get('shipping_system').patchValue(null);
      }
    }
  }

  toggleShowPaymentStatus(event: any) {
    const portalSettings = this.form.get('portal_settings');
    portalSettings.patchValue({
      show_payment_status_in_list: event.target.checked,
      show_payment_status_in_details: event.target.checked,
    });
  }

  toggleViewTransactions(event: any) {
    const portalSettings = this.form.get('portal_settings');
    portalSettings.patchValue({
      show_transactions_in_list: event.target.checked,
      show_transactions_in_details: event.target.checked,
    });
  }

  get canSelectCancelUpsell() {
    return !this.isFieldEqual('portal_settings.cancel_items_selection', CancelItemsSelection.ShowNone);
  }

  allowCancelUpsellSelection(event) {
    const portalSettings = this.form.get('portal_settings');
    portalSettings.patchValue({
      cancel_items_selection: event.target.checked
        ? CancelItemsSelection.UpsellsOnlyIfUpsell
        : CancelItemsSelection.ShowNone,
    });
  }

  get canSelectReturnUpsell() {
    return !this.isFieldEqual('portal_settings.return_items_selection', CancelItemsSelection.ShowNone);
  }

  allowReturnUpsellSelection(event) {
    const portalSettings = this.form.get('portal_settings');
    portalSettings.patchValue({
      return_items_selection: event.target.checked ? CancelItemsSelection.ShowAll : CancelItemsSelection.ShowNone,
    });
  }

  get showBreadcrumbs() {
    return !this.isFieldEqual('show_breadcrumbs', ShowBreadcrumbs.Never);
  }

  allowShowBreadcrumbs(event) {
    this.form.patchValue({
      show_breadcrumbs: event.target.checked ? ShowBreadcrumbs.OnlyIfCanUndo : ShowBreadcrumbs.Never,
    });
  }

  protected addDefaultContactFormFields() {
    const defaultFields = [
      { name: DefaultContactFormFields.FirstName, fieldName: DefaultContactFormFields.FirstName, type: 'text' },
      { name: DefaultContactFormFields.LastName, fieldName: DefaultContactFormFields.LastName, type: 'text' },
      { name: DefaultContactFormFields.ContactNumber, fieldName: DefaultContactFormFields.ContactNumber, type: 'phone' },
      { name: DefaultContactFormFields.Email, fieldName: RequiredEmailSupportFields.Email, type: 'email' },
      { name: DefaultContactFormFields.Message, fieldName: RequiredEmailSupportFields.Message, type: 'textarea' },
    ];

    const fields = this.form.get('support_email_fields') as FormArray;
    const existingFieldNames = new Set(
      fields.controls.map((field) => (field as FormGroup).controls['name'].value.toLowerCase())
    );

    defaultFields.forEach((defaultField) => {
      if (!existingFieldNames.has(defaultField.fieldName.toLowerCase())) {
        this.addContactFormField(defaultField.fieldName, defaultField.name, defaultField.type, true);
      }
    });
  }

  private createContactFormField(field: ContactFormField): FormGroup {
    return this.formBuilder.group({
      name: [field.name, [Validators.required]],
      label: [field.label, [Validators.required]],
      type: [field.type, [Validators.required]],
      required: [field.required],
      values: [
        field.values && field.values.length
          ? Array.isArray(field.values)
            ? field.values.join(', ')
            : field.values
          : '',
      ],
    });
  }

  addContactFormField(
    name: string = null,
    label: string = null,
    type: string = 'text',
    required: boolean = false,
    values: string[] = []
  ) {
    let fields = this.form.get('support_email_fields') as FormArray;
    fields.push(
      this.createContactFormField({
        name: name,
        label: label,
        type: type,
        required: required,
        values: values,
      } as ContactFormField)
    );
  }

  onFieldDrop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.supportEmailFields.controls, event.previousIndex, event.currentIndex);
    this.supportEmailFields.updateValueAndValidity();
  }

  removeContactFormField(index) {
    let fields = this.form.get('support_email_fields') as FormArray;
    fields.removeAt(index);
  }

  isContactFieldEditable(field: FormGroup): boolean {
    return (
      field.value.name !== RequiredEmailSupportFields.Email &&
      field.value.name !== RequiredEmailSupportFields.Message
    );
  }

  openContactFieldDialog(field: FormGroup = null) {
    if (field) {
      this.selectedContactField = field.value;
      this.isEditableField = this.isContactFieldEditable(field);
    }

    setTimeout(() => {
      this.modalService.getModal('contactFieldDialogCP').open();
    });
  }

  onSaveContactField() {
    if (this.contactFieldPopupRef) {
      const fieldData: ContactFormField = this.contactFieldPopupRef.getFormData();

      if (fieldData) {
        if (this.selectedContactField) {
          const index = this.supportEmailFields.controls.findIndex(
            (control) => control.value.name === this.selectedContactField.name
          );

          if (index !== -1) {
            this.supportEmailFields.at(index).patchValue(fieldData);
          }
        } else {
          this.addContactFormField(
            fieldData.name,
            fieldData.label,
            fieldData.type,
            fieldData.required,
            fieldData.values || []
          );
        }

        this.closeContactFieldDialog();
      } else {
        this.alertService.error('Please fill in all required fields', true);
      }
    } else {
      this.alertService.error('Unable to save field. Please try again.', true);
    }
  }

  closeContactFieldDialog() {
    this.selectedContactField = null;
    this.isEditableField = true;
    this.modalService.getModal('contactFieldDialogCP').close();
  }

  getFieldSelectOptions(field: FormGroup): string[] {
    const values = field.get('values').value;
    let result = [];
    if (values && typeof values === 'string') {
      if (field.get('type').value === this.emailSupportFieldTypes.Select) {
        result = values
          .split(',')
          .map((option) => option.trim())
          .filter((option) => option.length > 0);
      }
    }
    return result;
  }
}
